import React, { useEffect, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { formatISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import SearchIcon from "@/components/Icons/SearchIcon";
import { graphql } from "@/gql";
import { Maybe } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";
import BackToDashboardIcon from "@components/Icons/BackToDashboardIcon";

const useStyles = makeStyles()(() => ({
  paginationContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: 14,
  },
  navBar: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    margin: 4,
  },
  root: {
    display: "flex",
    backgroundColor: colors.white,
    marginLeft: 14,
    marginRight: 14,
    flexDirection: "column",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: "24px",
    paddingLeft: "18px",
    paddingRight: "18px",
    width: "100%",
  },
  table: {
    minWidth: 650,
  },
  actionContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const StyledBackToDashboardIcon = withStyles(BackToDashboardIcon, {
  root: {
    height: 24,
    width: 28,
    marginRight: 2,
  },
});

const StyledTextField = withStyles(TextField, {
  root: {
    width: "360px",
  },
});

const StyledTableHead = withStyles(TableHead, {
  root: {
    backgroundColor: "#EDF0F5",
    padding: 28,
  },
});

const StyledTableContainer = withStyles(TableContainer, {
  root: {
    marginTop: 14,
    overflowY: "auto",
    height: "100%",
    paddingBottom: 14,
  },
});

const StyledTableCell = withStyles(TableCell, {
  root: {
    borderRight: "2px solid #EDF0F5",
  },
});

const StyledCheckBox = withStyles(Checkbox, {
  root: {
    marginRight: 18,
  },
});
const StyledArrowDropUpIcon = withStyles(ArrowDropUpIcon, {
  root: {
    height: 14,
    width: 14,
    marginLeft: 12,
  },
});

const StyledArrowDropDownIcon = withStyles(ArrowDropDownIcon, {
  root: {
    height: 14,
    width: 14,
    marginLeft: 12,
  },
});

type SelectedCellsType = {
  id: string;
  email: Maybe<string> | undefined;
};

type CancelledInvitesType = {
  id: string;
  email: Maybe<string> | undefined;
};

type ErrorMessage = {
  __typename: "AppError";
  fullMessage: string;
} | null;

type RowsData = {
  clientId: string;
  clientName: string | undefined;
  email: Maybe<string> | undefined;
  requestedDate: Maybe<string> | undefined;
};

const pendingAppInvitePageQuery = graphql(`
  query PendingInvites {
    pendingInvites {
      nodes {
        id
        invitedAt
        profile {
          id
          fullName
        }
        email
      }
    }
  }
`);

const inviteClientMutation = graphql(`
  mutation InviteClient($input: InviteClientInput!) {
    inviteClient(input: $input) {
      clients {
        id
      }
    }
  }
`);

const cancelInviteMutation = graphql(`
  mutation CancelInvite($input: CancelInviteInput!) {
    cancelInvite(input: $input) {
      clients {
        id
      }
    }
  }
`);

const PendingAppInvitePage: React.FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { data, loading } = useQuery(pendingAppInvitePageQuery);
  const [searchText, setSearchText] = useState<string>("");
  const [resendInvite] = useMutation(inviteClientMutation);
  const [cancelInvite] = useMutation(cancelInviteMutation);
  const [allCellCheck, setAllCellCheck] = useState<boolean>(false);
  const [rowsData, setRowsData] = useState<RowsData[] | undefined>([]);
  const [selectedCells, setSelectedCells] = useState<SelectedCellsType[]>([]);
  const snackbar = useGlobalSnackbar();
  const [mutationErrorMessage, setMutationErrorMessage] = useState<
    ErrorMessage[]
  >([]);
  function onGoback() {
    navigate("/");
  }

  const pendingInvites = data?.pendingInvites.nodes;

  const rows = pendingInvites?.map((client) => {
    return {
      clientId: client.id,
      clientName: client?.profile?.fullName,
      email: client.email,
      requestedDate: formatISO(new Date(client.invitedAt ?? ""), {
        representation: "date",
      }),
    };
  });

  useEffect(() => {
    if (data) {
      setRowsData(rows);
    }
  }, [data]);

  const handleResendInvite = async (payload: SelectedCellsType) => {
    try {
      await resendInvite({
        variables: {
          input: { attributes: [{ id: payload.id, email: payload.email }] },
        },
        refetchQueries: [pendingAppInvitePageQuery],
      });
      snackbar.open(`App Invite was successfully resent to ${payload.email}`);
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          `An error has occured while sending the invite to ${payload.email}. Please try again later`
        )
      );
    }
  };

  const handleResendAllInvites = async () => {
    try {
      await resendInvite({
        variables: { input: { attributes: selectedCells } },
        refetchQueries: [pendingAppInvitePageQuery],
      });
      snackbar.open(
        `App Invites were successfully resent to ${selectedCells.length} client(s)`
      );
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          `An error has occured while sending the invites to your ${selectedCells.length} clients(s). Please try again later`
        )
      );
    }
    setAllCellCheck(false);
    setSelectedCells([]);
    setRowsData(rows);
  };

  const handleCancelAllInvite = async () => {
    const cancelInviteInputs = selectedCells.map((e) => e.id);
    try {
      await cancelInvite({
        variables: { input: { clientIds: cancelInviteInputs } },
        refetchQueries: [pendingAppInvitePageQuery],
      });

      snackbar.open(`${cancelInviteInputs.length} app invites were cancelled`);
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          `An error has occured while cancelling the invites to your ${cancelInviteInputs.length} client(s). Please try again later`
        )
      );
    }
    setAllCellCheck(false);
    setRowsData(rows);
    setSelectedCells([]);
  };

  const handleCancelInvite = async (
    payload: CancelledInvitesType,
    email: string
  ) => {
    try {
      await cancelInvite({
        variables: { input: { clientIds: [payload.id] } },
        refetchQueries: [pendingAppInvitePageQuery],
      });

      snackbar.open(`App invite was cancelled for ${email}`);
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          `An error has occured while cancelling the invite for ${email}. Please try again later`
        )
      );
    }
  };

  function handleSelectAllCell(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      if (rowsData != undefined) {
        const newlySelectedCells = rowsData.map((n) => {
          return { id: n.clientId, email: n.email };
        });

        setSelectedCells(newlySelectedCells);
        setAllCellCheck(true);
      }
    } else {
      setSelectedCells([]);
      setAllCellCheck(false);
    }
  }

  function handleClick(
    event: React.MouseEvent<unknown>,
    payload: SelectedCellsType
  ) {
    const selectedIndex = selectedCells.findIndex((e) => e.id == payload.id);
    let newSelected: SelectedCellsType[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedCells, payload);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedCells.slice(1));
    } else if (selectedIndex === selectedCells.length - 1) {
      newSelected = newSelected.concat(selectedCells.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedCells.slice(0, selectedIndex),
        selectedCells.slice(selectedIndex + 1)
      );
    }

    setSelectedCells(newSelected);
    if (newSelected.length > 1) {
      setAllCellCheck(true);
    }
    if (newSelected.length <= 1) {
      setAllCellCheck(false);
    }
  }

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchText(event.target.value);
    const searchWord = event.target.value.toLowerCase();
    if (searchWord !== "" && rowsData !== undefined) {
      const filteredRows = rowsData.filter((temp) => {
        return temp.clientName?.toLocaleLowerCase().includes(searchWord);
      });
      setRowsData(filteredRows);
    } else {
      setRowsData(rows);
    }
  }

  function getPendingInvitesTable() {
    if (rowsData) {
      if (rowsData.length > 0) {
        return (
          <StyledTableContainer>
            <Table className={classes.table} stickyHeader>
              <StyledTableHead>
                <TableRow>
                  <StyledTableCell align="left" style={{ color: colors.gray }}>
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <StyledCheckBox
                        color="primary"
                        onChange={handleSelectAllCell}
                        value={allCellCheck}
                        checked={allCellCheck}
                      />
                      Client Name
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <StyledArrowDropUpIcon />
                        <StyledArrowDropDownIcon />
                      </div>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell align="left" style={{ color: colors.gray }}>
                    Email
                  </StyledTableCell>
                  <StyledTableCell align="left" style={{ color: colors.gray }}>
                    <Box display="flex" flexDirection="row" alignItems="center">
                      Requested Date
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <StyledArrowDropUpIcon />
                        <StyledArrowDropDownIcon />
                      </div>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell style={{ color: colors.gray }}>
                    Action
                  </StyledTableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {rowsData?.map((row) => {
                  const isItemSelected = isSelected({
                    id: row.clientId,
                    email: row.email,
                  });
                  return (
                    <TableRow key={row.clientId}>
                      <StyledTableCell>
                        <StyledCheckBox
                          color="primary"
                          onClick={(event) =>
                            handleClick(event, {
                              id: row.clientId,
                              email: row.email,
                            })
                          }
                          checked={isItemSelected}
                        />
                        {row.clientName}
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        {row.email}
                      </StyledTableCell>
                      <StyledTableCell align="left" width={200}>
                        {row.requestedDate}
                      </StyledTableCell>
                      <StyledTableCell align="left" width={250}>
                        <Box className={classes.actionContainer}>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              handleResendInvite({
                                id: row.clientId,
                                email: row.email,
                              })
                            }
                          >
                            Resend Invite
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              handleCancelInvite(
                                { id: row.clientId, email: row.email },
                                row.email ?? ""
                              )
                            }
                          >
                            Cancel
                          </Button>
                        </Box>
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
        );
      } else {
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={14}
          >
            <Typography variant="h6" color="textSecondary">
              No pending invites
            </Typography>
          </Box>
        );
      }
    }
  }

  const isSelected = (payload: SelectedCellsType) =>
    selectedCells.findIndex((e) => e.id == payload.id) !== -1;
  return (
    <>
      <Box className={classes.navBar}>
        <IconButton onClick={onGoback} size="large">
          <StyledBackToDashboardIcon />
        </IconButton>
        <Typography variant="h6">Back to dashboard</Typography>
      </Box>
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h5">
            Pending App invite({rowsData?.length})
          </Typography>
          {allCellCheck ? (
            <Box className={classes.actionContainer}>
              <Button
                variant="outlined"
                style={{ marginRight: 24 }}
                onClick={handleResendAllInvites}
              >
                Resend Invite
              </Button>
              <Button variant="outlined" onClick={handleCancelAllInvite}>
                Cancel
              </Button>
            </Box>
          ) : (
            <StyledTextField
              placeholder="Search..."
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              value={searchText}
              variant="outlined"
            />
          )}
        </Box>
        {!loading ? (
          getPendingInvitesTable()
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={14}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>

      <Dialog
        open={mutationErrorMessage.length > 0}
        onClose={() => setMutationErrorMessage([])}
        maxWidth="sm"
      >
        <Box style={{ padding: 12 }} display="flex" flexDirection="row">
          <Box display="flex" flexDirection="column">
            {mutationErrorMessage.map((error) => {
              return (
                <Typography
                  key={error?.fullMessage}
                  variant="subtitle1"
                  color="error"
                  style={{ marginRight: 12 }}
                >
                  {error?.fullMessage}
                </Typography>
              );
            })}
          </Box>
          <IconButton
            style={{ height: 4, width: 4 }}
            onClick={() => setMutationErrorMessage([])}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Dialog>
    </>
  );
};
export default PendingAppInvitePage;
